import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import bankData from '../common/BankData'; 
import bankIllustration from '../assets/logoPB.png';
import Consultation from './Consultation';
import useSeo from "../hooks/useSeo";

const baseStyles = `
.partners {
  background: #F0F7FF;
  padding: 8rem 1rem;
  display: flex;
  justify-content: center;
  min-height: 640px;
  font-family: 'Segoe UI', sans-serif;
}
.partners-inner {
  background: #27409C;
  border-radius: 1.5rem;
  padding: 3rem 2rem;
  color: #fff;
  position: relative;
  width: 100%;
  max-width: 1300px;
  box-sizing: border-box;
  text-align: center;
}
.partners-inner h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  letter-spacing: 0.5px;
}
.partners-inner p {
  font-size: 1.05rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.4;
}
.illustration {
  width: 110px;
  margin: 0 auto 2rem;
  display: block;
  filter: drop-shadow(0 0 6px rgba(255,255,255,0.4));
}
.bank-logos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  justify-content: center;
}
.bank-card {
  background: #fff;
  border-radius: 0.55rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 95px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  cursor: pointer;
}
.bank-card:hover {
  transform: scale(1.03);
}
/* 🔧 Constrain logo width too, so extra-wide images don't appear huge */
.bank-card img {
  max-height: 40px;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}
@media(min-width: 768px) {
  .partners-inner {
    text-align: left;
    padding: 5rem 3rem;
  }
  .partners-inner h2 {
    font-size: 3.5rem;
  }
  .partners-inner p {
    font-size: 1.4rem;
    margin-bottom: 12rem;
  }
  .illustration {
    position: absolute;
    top: 1rem;
    right: 4rem;
    width: 350px;
  }
  .bank-logos {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 2rem;
  }
  .bank-card {
    min-height: 100px;
    padding: 1.5rem;
  }
  /* 🔧 Desktop constraint mirrors base rule */
  .bank-card img {
    max-height: 50px;
    max-width: 100%;
    width: auto;
    height: auto;
  }
}
`;

const detailStyles = `
.partners-detail {
  background: #F0F7FF;
  padding: 6rem 1rem 6rem;
  min-height: 800px;
  font-family: 'Segoe UI', sans-serif;
  text-align: center;
}
.partners-detail h2 {
  font-size: 1.6rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 1rem;
}
.partners-detail p {
  max-width: 700px;
  margin: 0 auto 3rem;
  color: #555;
  line-height: 1.6;
  font-size: 1rem;
}
@media(min-width: 768px) {
  .partners-detail h2 {
    font-size: 3rem;
  }
  .partners-detail p {
    font-size: 1.2rem;
    margin-bottom: 3rem;
  }
}
.bank-logos-detail {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto 5rem;
}
.bank-card-detail {
  background: #fff;
  border-radius: 0.75rem;
  padding: 2.5rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  position: relative;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
}
.bank-card-detail:hover {
  transform: perspective(500px) rotateX(2deg) rotateY(2deg);
  background-color: #d8eafe;
  box-shadow: 0 12px 24px rgba(0,0,0,0.15);
}
.bank-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}
.bank-card-detail h3 {
  font-size: 1.4rem;
  color: #111;
  margin: 0;
  font-weight: 600;
}
.bank-card-detail p {
  margin: 0;
  color: #555;
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
}
.bank-logo {
  max-width: 80px;
  height: auto;
  object-fit: contain;
}
.consultation-wrapper {
  background: #fff;
  width: 100%;
  padding: 5rem 1rem;
}
`;

export default function PartnerBanks() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  // ✅ SEO integration
  useSeo({
    title: isHome ? 'Partner Banks | CardAcademy' : 'Partner Bank Details | CardAcademy',
    description: isHome 
      ? 'Explore credit card options from our trusted banking partners.' 
      : 'Explore the premium credit card options offered by our trusted banking partners.',
    canonical: window.location.href,
  });

  const goToBank = (bank) => {
    const slug = bank.name.toLowerCase().replace(/\s+/g, '-');
    navigate(`/partner-banks/${slug}`, { state: { bank } });
  };

  return (
    <>
      <style>{isHome ? baseStyles : detailStyles}</style>

      {isHome ? (
        <section className="partners">
          <div className="partners-inner">
            <h2>Our Partner Banks</h2>
            <p>Explore credit card options from our trusted banking partners.</p>

            <img
              src={bankIllustration}
              alt="Partner Bank Illustration"
              className="illustration"
              style={{ width: '270px' }}
            />

            <div className="bank-logos">
              {bankData.map(bank => (
                <div key={bank.id} className="bank-card" onClick={() => goToBank(bank)}>
                  <img src={bank.logo} alt={bank.name} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="partners-detail">
            <h2>Our Partner Banks</h2>
            <p>
              Explore credit card options from our trusted banking partners—each
              offering unique benefits and rewards tailored to your financial needs.
            </p>

            <div className="bank-logos-detail">
              {bankData.map(bank => (
                <div key={bank.id} className="bank-card-detail" onClick={() => goToBank(bank)}>
                  <div className="bank-card-header">
                    <h3>{bank.name}</h3>
                    <img src={bank.logo} alt={bank.name} className="bank-logo" />
                  </div>
                  <p>{bank.description}</p> 
                </div>
              ))}
            </div>
          </section>

          <div className="consultation-wrapper">
            <Consultation />
          </div>
        </>
      )}
    </>
  );
}
